import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { GlassCard, GlassCardContent, GlassCardDescription, GlassCardHeader, GlassCardTitle } from '@easyflow/ui-master/components/ui/glass-card';
import { Button } from '@easyflow/ui-master/components/ui/button';
import { Input } from '@easyflow/ui-master/components/ui/input';
import { UppercaseInput } from '@/components/UppercaseInput';
import { Label } from '@easyflow/ui-master/components/ui/label';
import { Switch } from '@easyflow/ui-master/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@easyflow/ui-master/components/ui/select';
import { Plus, Trash2, ArrowLeft, ArrowRight, Save, User, Building2, Kanban, CheckCircle, Eye, EyeOff, RefreshCw, Copy, Check, ShieldCheck, X } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import apiClient from '@/lib/api';
import AddressAutocomplete from '@easyflow/ui-frontend/components/AddressAutocomplete';
import { masterAddressFetch } from '@/lib/masterAddressFetch';
import { toErr } from '@easyflow/shared';
import { buildDefaultSipDomain } from '@easyflow/shared';

interface AdminUser {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  role: 'admin' | 'super_admin';
}

interface Company {
  legal_name: string;
  vat_number: string;
  tax_code: string;
  address: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
}

const PASSWORD_CRITERIA = [
  { key: 'length', label: 'Almeno 12 caratteri', test: (p: string) => p.length >= 12 },
  { key: 'upper', label: 'Almeno una maiuscola (A-Z)', test: (p: string) => /[A-Z]/.test(p) },
  { key: 'lower', label: 'Almeno una minuscola (a-z)', test: (p: string) => /[a-z]/.test(p) },
  { key: 'number', label: 'Almeno un numero (0-9)', test: (p: string) => /[0-9]/.test(p) },
  { key: 'special', label: 'Almeno un carattere speciale (!@#$%...)', test: (p: string) => /[!@#$%^&*()_+\-=\[\]{}|;:,.<>?/~`]/.test(p) },
];

function generateSecurePassword(length = 16): string {
  const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const lower = 'abcdefghijklmnopqrstuvwxyz';
  const digits = '0123456789';
  const special = '!@#$%^&*_+-=?';
  const all = upper + lower + digits + special;

  const cryptoRandom = (max: number) => {
    const arr = new Uint32Array(1);
    crypto.getRandomValues(arr);
    return (arr[0] ?? 0) % max;
  };
  const getRandomChar = (chars: string): string => chars.charAt(cryptoRandom(chars.length));

  let password = [
    getRandomChar(upper),
    getRandomChar(upper),
    getRandomChar(lower),
    getRandomChar(lower),
    getRandomChar(digits),
    getRandomChar(digits),
    getRandomChar(special),
    getRandomChar(special),
  ];

  for (let i = password.length; i < length; i++) {
    password.push(getRandomChar(all));
  }

  for (let i = password.length - 1; i > 0; i--) {
    const j = cryptoRandom(i + 1);
    const pi = password[i] ?? '';
    const pj = password[j] ?? '';
    password[i] = pj;
    password[j] = pi;
  }

  return password.join('');
}

function getPasswordStrength(password: string): { score: number; label: string; color: string } {
  const passed = PASSWORD_CRITERIA.filter(c => c.test(password)).length;
  if (passed <= 1) return { score: 1, label: 'Molto debole', color: 'bg-red-500' };
  if (passed <= 2) return { score: 2, label: 'Debole', color: 'bg-orange-500' };
  if (passed <= 3) return { score: 3, label: 'Discreta', color: 'bg-yellow-500' };
  if (passed <= 4) return { score: 4, label: 'Buona', color: 'bg-blue-500' };
  return { score: 5, label: 'Eccellente', color: 'bg-green-500' };
}

interface PipelineEntry {
  id: string;
  company_name?: string;
  contact_name?: string;
  contact_email?: string;
  contact_phone?: string;
  vat_number?: string;
  fiscal_code?: string;
  legal_address?: string;
  legal_city?: string;
  legal_province?: string;
  legal_zip?: string;
  contact_first_name?: string;
  contact_last_name?: string;
  contract_number?: string;
  contract_signed_at?: string;
  opportunity_value?: string | number;
}

export default function CreateTenant() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);

  // Form data
  const [tenantData, setTenantData] = useState({
    name: '',
    slug: '',
    domain: '',
    sip_domain: '',
    context_prefix: '',
    timezone: 'Europe/Rome',
    language: 'it',
    max_extensions: 100,
    max_trunks: 10,
    max_concurrent_calls: 20,
    max_users: 50,
    max_ai_agents: 10,
  });

  const [adminUser, setAdminUser] = useState<AdminUser>({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    role: 'admin'
  });

  const [companies, setCompanies] = useState<Company[]>([
    {
      legal_name: '',
      vat_number: '',
      tax_code: '',
      address: '',
      city: '',
      state: '',
      postal_code: '',
      country: 'Italy',
    }
  ]);

  const [showPassword, setShowPassword] = useState(false);
  const [passwordCopied, setPasswordCopied] = useState(false);

  const [enablePosWarehouse, setEnablePosWarehouse] = useState(true);
  const [pipelineEntries, setPipelineEntries] = useState<PipelineEntry[]>([]);
  const [selectedLeadId, setSelectedLeadId] = useState('');

  useEffect(() => {
    apiClient.get<PipelineEntry[]>('/master/tenants/linkable-leads').then((res) => {
      if (res.success && res.data) {
        setPipelineEntries(Array.isArray(res.data) ? res.data : []);
      }
    }).catch(() => {});
  }, []);

  const handleSelectLead = (leadId: string) => {
    if (leadId === '__none') {
      setSelectedLeadId('');
      return;
    }
    setSelectedLeadId(leadId);
    if (!leadId) return;
    const lead = pipelineEntries.find(e => e.id === leadId);
    if (!lead) return;

    if (lead.company_name) {
      handleTenantDataChange('name', lead.company_name);
      setCompanies(prev => {
        const updated = [...prev];
        updated[0] = {
          ...updated[0],
          legal_name: lead.company_name || '',
          vat_number: lead.vat_number || '',
          tax_code: lead.fiscal_code || '',
          address: lead.legal_address || '',
          city: lead.legal_city || '',
          state: lead.legal_province || '',
          postal_code: lead.legal_zip || '',
          country: 'Italy',
        };
        return updated;
      });
    }

    const firstName = lead.contact_first_name || (lead.contact_name || '').split(' ')[0] || '';
    const lastName = lead.contact_last_name || (lead.contact_name || '').split(' ').slice(1).join(' ') || '';

    if (lead.contact_email) {
      setAdminUser(prev => ({ ...prev, email: lead.contact_email || prev.email, first_name: firstName || prev.first_name, last_name: lastName || prev.last_name }));
    }

    toast({ title: 'Dati importati', description: `Dati amministrativi pre-compilati dalla lead "${lead.company_name || lead.contact_name}"` });
  };

  const steps = [
    { id: 1, title: 'Informazioni Tenant', icon: Building2 },
    { id: 2, title: 'Utente Admin', icon: User },
    { id: 3, title: 'Ragioni Sociali', icon: Building2 }
  ];

  const handleTenantDataChange = (field: string, value: string | number) => {
    setTenantData(prev => ({ ...prev, [field]: value }));
    
    // Auto-generate slug, sip_domain, context_prefix from name
    if (field === 'name') {
      const slug = (value as string).toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .trim();
      setTenantData(prev => ({ 
        ...prev, 
        slug,
        domain: slug,
        sip_domain: buildDefaultSipDomain(slug),
        context_prefix: `tenant-${slug}`
      }));
    }
    
    // Also auto-generate when slug is directly changed
    if (field === 'slug') {
      const slug = (value as string).toLowerCase().replace(/[^a-z0-9-]/g, '');
      setTenantData(prev => ({
        ...prev,
        slug,
        sip_domain: buildDefaultSipDomain(slug),
        context_prefix: `tenant-${slug}`
      }));
    }
  };

  const handleAdminUserChange = (field: keyof AdminUser, value: string) => {
    setAdminUser(prev => ({ ...prev, [field]: value }));
  };

  const handleCompanyChange = (index: number, field: keyof Company, value: string | boolean) => {
    setCompanies(prev => prev.map((company, i) => 
      i === index ? { ...company, [field]: value } : company
    ));
  };

  const addCompany = () => {
    setCompanies(prev => [...prev, {
      legal_name: '',
      vat_number: '',
      tax_code: '',
      address: '',
      city: '',
      state: '',
      postal_code: '',
      country: 'Italy',
    }]);
  };

  const removeCompany = (index: number) => {
    if (companies.length > 1) {
      setCompanies(prev => prev.filter((_, i) => i !== index));
    }
  };

  const validateStep = (step: number): boolean => {
    switch (step) {
      case 1:
        return !!(tenantData.name && tenantData.slug && tenantData.domain && tenantData.sip_domain);
      case 2: {
        const allCriteriaMet = PASSWORD_CRITERIA.every(c => c.test(adminUser.password));
        return !!(adminUser.first_name && adminUser.last_name && adminUser.email && adminUser.password && allCriteriaMet);
      }
      case 3:
        return companies.every(c => c.legal_name);
      default:
        return false;
    }
  };

  const handleNext = () => {
    const isValid = validateStep(currentStep);
    
    if (isValid) {
      setCurrentStep(prev => Math.min(prev + 1, steps.length));
    } else {
      toast({
        title: "Errore",
        description: "Compila tutti i campi obbligatori prima di procedere",
        variant: "destructive",
      });
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateStep(3)) {
      toast({
        title: "Errore",
        description: "Compila tutti i campi obbligatori",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      const result = await apiClient.createTenantWithCompanies({
        ...tenantData,
        admin_user: adminUser,
        companies,
        modules: {
          pos_warehouse: enablePosWarehouse,
        },
        settings: {
          max_extensions: tenantData.max_extensions,
          max_trunks: tenantData.max_trunks,
          max_concurrent_calls: tenantData.max_concurrent_calls,
          max_users: tenantData.max_users,
          max_ai_agents: tenantData.max_ai_agents,
          recording_enabled: true,
          gdpr_compliant: true,
          voicemail_directory: `/var/lib/freeswitch/storage/${tenantData.slug}/voicemail`
        }
      });

      const resultData = result as { data?: { id?: string }; id?: string };
      const tenantId = resultData?.data?.id || resultData?.id;
      if (selectedLeadId && tenantId) {
        try {
          await apiClient.post(`/master/tenants/${tenantId}/link-lead`, { entry_id: selectedLeadId });
        } catch (linkErr: unknown) {
          const __linkErr = toErr(linkErr);
          console.warn('Link lead-tenant fallito:', __linkErr.message);
        }
      }

      toast({
        title: "Successo!",
        description: `Tenant ${tenantData.name} creato con successo.${selectedLeadId ? ' Contratto collegato automaticamente.' : ' Contesti FreeSWITCH generati automaticamente.'}`,
      });

      navigate('/tenants');
    } catch (error: unknown) {
      const __error = toErr(error);
      toast({
        title: "Errore",
        description: __error.message || "Errore durante la creazione del tenant",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            {pipelineEntries.length > 0 && (
              <div className="p-4 rounded-xl border-2 border-dashed border-blue-300 dark:border-blue-600/50 bg-blue-50/50 dark:bg-blue-900/10 space-y-3">
                <div className="flex items-center gap-2">
                  <Kanban className="h-5 w-5 text-blue-500" />
                  <h3 className="text-sm font-semibold text-blue-700 dark:text-blue-400">Collega a Contratto Finalizzato</h3>
                  <span className="text-xs text-blue-500/70 dark:text-blue-400/50">({pipelineEntries.length} contratti disponibili)</span>
                </div>
                <div className="flex items-center gap-3">
                  <Select value={selectedLeadId} onValueChange={handleSelectLead}>
                    <SelectTrigger className="flex-1">
                      <SelectValue placeholder="Seleziona un contratto finalizzato per pre-compilare i dati..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none">Nessuno — inserisci manualmente</SelectItem>
                      {pipelineEntries.map((e) => (
                        <SelectItem key={e.id} value={e.id}>
                          {e.company_name || e.contact_name}{e.contract_number ? ` — Contr. ${e.contract_number}` : ''}{e.contract_signed_at ? ` (${new Date(e.contract_signed_at).toLocaleDateString('it-IT')})` : ''}{e.opportunity_value ? ` — €${Number(e.opportunity_value).toLocaleString('it-IT')}` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedLeadId && selectedLeadId !== '__none' && (
                    <div className="flex items-center gap-1.5 text-green-600 dark:text-green-400">
                      <CheckCircle className="h-4 w-4" />
                      <span className="text-xs font-medium">Collegato</span>
                    </div>
                  )}
                </div>
                <p className="text-xs text-blue-500/60 dark:text-blue-400/40">I dati verranno pre-compilati: ragione sociale, P.IVA, CF, contatto, indirizzo. Potrai modificarli nei passi successivi.</p>
              </div>
            )}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Informazioni Generali</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Nome Tenant *</Label>
                  <UppercaseInput
                    id="name"
                    value={tenantData.name}
                    onChange={(v) => handleTenantDataChange('name', v)}
                    placeholder="Es. Demo Tenant"
                  />
                </div>
                <div>
                  <Label htmlFor="slug">Slug Tenant * (solo lettere minuscole, numeri, trattini)</Label>
                  <Input
                    id="slug"
                    value={tenantData.slug}
                    onChange={(e) => handleTenantDataChange('slug', e.target.value)}
                    placeholder="Es. demo"
                  />
                  <p className="text-sm text-muted-foreground mt-1">Verrà usato per generare il dominio SIP</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="domain">Dominio (per autenticazione web) *</Label>
                  <Input
                    id="domain"
                    value={tenantData.domain}
                    onChange={(e) => handleTenantDataChange('domain', e.target.value)}
                    placeholder="Es. demo"
                  />
                </div>
                <div>
                  <Label htmlFor="sip_domain">Dominio SIP (auto-generato)</Label>
                  <Input
                    id="sip_domain"
                    value={tenantData.sip_domain}
                    disabled
                    className="bg-muted"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="context_prefix">Prefisso Contesti FreeSWITCH (auto-generato)</Label>
                  <Input
                    id="context_prefix"
                    value={tenantData.context_prefix}
                    disabled
                    className="bg-muted"
                  />
                </div>
                <div>
                  <Label htmlFor="timezone">Fuso Orario</Label>
                  <Select value={tenantData.timezone} onValueChange={(val) => handleTenantDataChange('timezone', val)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Europe/Rome">Europe/Rome (IT)</SelectItem>
                      <SelectItem value="Europe/London">Europe/London (UK)</SelectItem>
                      <SelectItem value="Europe/Paris">Europe/Paris (FR)</SelectItem>
                      <SelectItem value="Europe/Berlin">Europe/Berlin (DE)</SelectItem>
                      <SelectItem value="America/New_York">America/New_York (US-EST)</SelectItem>
                      <SelectItem value="UTC">UTC</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <Label htmlFor="language">Lingua IVR</Label>
                <Select value={tenantData.language} onValueChange={(val) => handleTenantDataChange('language', val)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="it">Italiano</SelectItem>
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="es">Español</SelectItem>
                    <SelectItem value="fr">Français</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-4 border-t pt-4">
              <h3 className="text-lg font-semibold">Limiti Tenant</h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="max_extensions">Max Extensions</Label>
                  <Input
                    id="max_extensions"
                    type="number"
                    min="1"
                    max="1000"
                    value={tenantData.max_extensions}
                    onChange={(e) => handleTenantDataChange('max_extensions', parseInt(e.target.value) || 100)}
                  />
                </div>
                <div>
                  <Label htmlFor="max_trunks">Max Trunks</Label>
                  <Input
                    id="max_trunks"
                    type="number"
                    min="1"
                    max="50"
                    value={tenantData.max_trunks}
                    onChange={(e) => handleTenantDataChange('max_trunks', parseInt(e.target.value) || 10)}
                  />
                </div>
                <div>
                  <Label htmlFor="max_concurrent_calls">Max Chiamate Simultanee</Label>
                  <Input
                    id="max_concurrent_calls"
                    type="number"
                    min="1"
                    max="1000"
                    value={tenantData.max_concurrent_calls}
                    onChange={(e) => handleTenantDataChange('max_concurrent_calls', parseInt(e.target.value) || 20)}
                  />
                </div>
                <div>
                  <Label htmlFor="max_users">Max Utenti</Label>
                  <Input
                    id="max_users"
                    type="number"
                    min="1"
                    max="10000"
                    value={tenantData.max_users}
                    onChange={(e) => handleTenantDataChange('max_users', parseInt(e.target.value) || 50)}
                  />
                </div>
                <div>
                  <Label htmlFor="max_ai_agents">Max Agenti AI (totale)</Label>
                  <Input
                    id="max_ai_agents"
                    type="number"
                    min="1"
                    max="1000"
                    value={tenantData.max_ai_agents}
                    onChange={(e) => handleTenantDataChange('max_ai_agents', parseInt(e.target.value) || 10)}
                  />
                  <p className="text-xs text-muted-foreground mt-1">Aggregato Voice + Chatbot + Personal Assistant + ElevenLabs.</p>
                </div>
              </div>
            </div>

            <div className="space-y-4 border-t pt-4">
              <h3 className="text-lg font-semibold">Moduli Opzionali</h3>
              <div className="flex items-center space-x-3">
                <Switch
                  id="pos_warehouse"
                  checked={enablePosWarehouse}
                  onCheckedChange={setEnablePosWarehouse}
                />
                <Label htmlFor="pos_warehouse" className="cursor-pointer">
                  Cassa e Magazzino (POS + Inventario + Listini + Lotti)
                </Label>
              </div>
              <p className="text-sm text-muted-foreground">
                Abilita il modulo POS con gestione magazzino, articoli, fornitori, listini prezzi e punto cassa.
              </p>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="first_name">Nome *</Label>
                <UppercaseInput
                  id="first_name"
                  value={adminUser.first_name}
                  onChange={(v) => handleAdminUserChange('first_name', v)}
                  placeholder="Nome"
                />
              </div>
              <div>
                <Label htmlFor="last_name">Cognome *</Label>
                <UppercaseInput
                  id="last_name"
                  value={adminUser.last_name}
                  onChange={(v) => handleAdminUserChange('last_name', v)}
                  placeholder="Cognome"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={adminUser.email}
                onChange={(e) => handleAdminUserChange('email', e.target.value)}
                placeholder="admin@azienda-demo.local"
              />
            </div>
            <div className="space-y-3">
              <Label htmlFor="password">Password *</Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={adminUser.password}
                    onChange={(e) => handleAdminUserChange('password', e.target.value)}
                    placeholder="Minimo 12 caratteri, maiuscole, numeri, speciali"
                    className="pr-10 font-mono"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  title="Genera password sicura"
                  onClick={() => {
                    const pwd = generateSecurePassword(16);
                    handleAdminUserChange('password', pwd);
                    setShowPassword(true);
                    setPasswordCopied(false);
                  }}
                >
                  <RefreshCw className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  title="Copia password"
                  disabled={!adminUser.password}
                  onClick={() => {
                    void navigator.clipboard.writeText(adminUser.password);
                    setPasswordCopied(true);
                    setTimeout(() => setPasswordCopied(false), 2000);
                    toast({ title: 'Password copiata', description: 'La password è stata copiata negli appunti' });
                  }}
                >
                  {passwordCopied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>

              {adminUser.password && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-300 ${getPasswordStrength(adminUser.password).color}`}
                        style={{ width: `${(getPasswordStrength(adminUser.password).score / 5) * 100}%` }}
                      />
                    </div>
                    <span className="text-xs font-medium text-muted-foreground min-w-[80px] text-right">
                      {getPasswordStrength(adminUser.password).label}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 gap-1">
                    {PASSWORD_CRITERIA.map(criterion => {
                      const passed = criterion.test(adminUser.password);
                      return (
                        <div key={criterion.key} className="flex items-center gap-1.5 text-xs">
                          {passed ? (
                            <CheckCircle className="h-3.5 w-3.5 text-green-500 shrink-0" />
                          ) : (
                            <X className="h-3.5 w-3.5 text-red-400 shrink-0" />
                          )}
                          <span className={passed ? 'text-green-600' : 'text-muted-foreground'}>
                            {criterion.label}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <ShieldCheck className="h-3.5 w-3.5" />
                L'utente dovrà cambiare la password al primo accesso
              </p>
            </div>
            <div>
              <Label htmlFor="role">Ruolo</Label>
              <Select value={adminUser.role} onValueChange={(value: 'admin' | 'super_admin') => handleAdminUserChange('role', value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">L'admin del tenant viene creato automaticamente con ruolo Admin</p>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold">Ragioni Sociali</h3>
              <Button onClick={addCompany} size="sm">
                <Plus className="h-4 w-4 mr-2" />
                Aggiungi
              </Button>
            </div>
            {companies.map((company, index) => (
              <GlassCard key={index}>
                <GlassCardHeader className="pb-3">
                  <div className="flex justify-between items-center">
                    <GlassCardTitle className="text-base">Azienda {index + 1}</GlassCardTitle>
                    <div className="flex items-center space-x-2">
                      {companies.length > 1 && (
                        <Button
                          onClick={() => removeCompany(index)}
                          variant="outline"
                          size="sm"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </GlassCardHeader>
                <GlassCardContent className="space-y-4">
                  <div>
                    <Label>Ragione Sociale *</Label>
                    <UppercaseInput
                      value={company.legal_name}
                      onChange={(v) => handleCompanyChange(index, 'legal_name', v)}
                      placeholder="Ragione sociale"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Partita IVA</Label>
                      <Input
                        value={company.vat_number}
                        onChange={(e) => handleCompanyChange(index, 'vat_number', e.target.value)}
                        placeholder="IT12345678901"
                      />
                    </div>
                    <div>
                      <Label>Codice Fiscale</Label>
                      <Input
                        value={company.tax_code}
                        onChange={(e) => handleCompanyChange(index, 'tax_code', e.target.value)}
                        placeholder="RSSMRA80A01H501U"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Indirizzo</Label>
                    <AddressAutocomplete
                      defaultValue={company.address}
                      placeholder="Via, numero civico"
                      fetchSuggestions={masterAddressFetch}
                      onAddressSelect={(a) => {
                        handleCompanyChange(index, 'address', a.address ?? '');
                        if (a.city !== undefined) handleCompanyChange(index, 'city', a.city);
                        if (a.province !== undefined) handleCompanyChange(index, 'state', a.province);
                        if (a.postal_code !== undefined) handleCompanyChange(index, 'postal_code', a.postal_code);
                        if (a.country !== undefined) handleCompanyChange(index, 'country', a.country);
                      }}
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label>Città</Label>
                      <Input
                        value={company.city}
                        onChange={(e) => handleCompanyChange(index, 'city', e.target.value)}
                        placeholder="Milano"
                      />
                    </div>
                    <div>
                      <Label>Provincia</Label>
                      <Input
                        value={company.state}
                        onChange={(e) => handleCompanyChange(index, 'state', e.target.value)}
                        placeholder="MI"
                      />
                    </div>
                    <div>
                      <Label>CAP</Label>
                      <Input
                        value={company.postal_code}
                        onChange={(e) => handleCompanyChange(index, 'postal_code', e.target.value)}
                        placeholder="20100"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Paese</Label>
                    <Input
                      value={company.country}
                      onChange={(e) => handleCompanyChange(index, 'country', e.target.value)}
                      placeholder="Italy"
                    />
                  </div>
                </GlassCardContent>
              </GlassCard>
            ))}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Crea Nuovo Tenant</h1>
          <p className="text-muted-foreground">
            Configura un nuovo tenant con utenza admin e ragioni sociali
          </p>
        </div>
        <Button variant="outline" onClick={() => navigate('/tenants')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Torna ai Tenant
        </Button>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center space-x-4">
        {steps.map((step, index) => {
          const Icon = step.icon;
          const isActive = currentStep === step.id;
          const isCompleted = currentStep > step.id;
          
          return (
            <div key={step.id} className="flex items-center">
              <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
                isActive ? 'border-blue-600 bg-blue-600 text-white' :
                isCompleted ? 'border-green-600 bg-green-600 text-white' :
                'border-border bg-white text-muted-foreground'
              }`}>
                <Icon className="h-5 w-5" />
              </div>
              <div className="ml-3">
                <p className={`text-sm font-medium ${
                  isActive ? 'text-blue-600' : isCompleted ? 'text-green-600' : 'text-muted-foreground'
                }`}>
                  {step.title}
                </p>
              </div>
              {index < steps.length - 1 && (
                <div className={`w-8 h-0.5 mx-4 ${
                  isCompleted ? 'bg-green-600' : 'bg-gray-300'
                }`} />
              )}
            </div>
          );
        })}
      </div>

      {/* Step Content */}
      <GlassCard>
        <GlassCardHeader>
          <GlassCardTitle className="flex items-center">
            {steps[currentStep - 1] && React.createElement(steps[currentStep - 1]!.icon, { className: "h-5 w-5 mr-2" })}
            {steps[currentStep - 1]?.title}
          </GlassCardTitle>
          <GlassCardDescription>
            {currentStep === 1 && "Configura le informazioni base del tenant"}
            {currentStep === 2 && "Crea l'utenza amministratore per il tenant"}
            {currentStep === 3 && "Aggiungi le ragioni sociali associate al tenant"}
            {currentStep === 4 && "Definisci i contatti di riferimento"}
          </GlassCardDescription>
        </GlassCardHeader>
        <GlassCardContent>
          {renderStepContent()}
        </GlassCardContent>
      </GlassCard>

      {/* Navigation */}
      <div className="flex justify-between">
        <Button
          variant="outline"
          onClick={handlePrevious}
          disabled={currentStep === 1}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Precedente
        </Button>
        
        <div className="flex space-x-2">
          {currentStep < steps.length ? (
            <Button onClick={handleNext}>
              Avanti
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={isLoading}>
              <Save className="h-4 w-4 mr-2" />
              {isLoading ? 'Creazione...' : 'Crea Tenant'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
